import { AssistantAdapter, HostAdapter } from "../types";
import { readCheckOutIdentity, writeCheckOutIdentity } from "./identity";
import { getPresentationAsPptx, describeSaveUnavailable, PPTX_MIME_TYPE } from "./bytes";
import { DeckModel, POWERPOINT_SEGMENT_ID_PATTERN } from "./deckModel";
import { extractDeck } from "./extractDeck";
import { powerPointToolSchemas, powerPointSystemPrompt } from "./toolSchemas";
import { executeToolCall, describeDeckEdit } from "./toolExecutor";
import { enrichCitations, reveal } from "./citations";

// PowerPoint: check out, Save, Check in, and the assistant.
//
// No `assembly`: reference placeholders are Word content controls. PowerPoint shape tags
// are a credible future home for the same idea, but that is a product design, not a port.
//
// Notably this is the only host where a full check out works in a browser: PowerPoint is
// the one application whose getFileAsync supports compressed bytes on the web, and
// PowerPointApi 1.7 is available there too.

/**
 * The assistant, minus one member: `review` is absent.
 *
 * PowerPoint has no revisions API -- no tracked changes, no per-edit accept or reject --
 * so there is nothing for the chat's accept/reject buttons to act on, and the chat hides
 * them when `review` is undefined. The alternative would be a synthetic undo stack in the
 * add-in, which would have to survive the user editing the deck underneath it and would
 * quietly stop matching reality the first time they did. PowerPoint's own Ctrl+Z is the
 * honest answer, and the system prompt tells the model to behave accordingly: make the
 * asked-for change and nothing else, and ask before deleting anything.
 */
const powerPointAssistant: AssistantAdapter = {
  extractDocumentModel: extractDeck,
  toolSchemas: powerPointToolSchemas,
  systemPrompt: powerPointSystemPrompt,
  executeToolCall,
  describeEdit: (toolName, rawArgs, result, docModel) =>
    describeDeckEdit(toolName, rawArgs, result, docModel as DeckModel),
  suggestedPrompts: [
    "Summarise this deck in a paragraph",
    "Suggest a clearer title for each slide",
    "Check the slides for inconsistent terminology",
    "Make the text on the busiest slide more concise",
  ],
  segmentIdPattern: POWERPOINT_SEGMENT_ID_PATTERN,
  enrichCitations: (citations, docModel) => enrichCitations(citations, docModel as DeckModel),
  reveal,
};

export const powerPointAdapter: HostAdapter = {
  kind: "powerpoint",
  displayName: "PowerPoint",
  capabilities: {
    assembly: false,
  },

  manifestFileName: "manifest.powerpoint.xml",

  documents: {
    fileExtension: "pptx",
    mimeType: PPTX_MIME_TYPE,
    readCheckOutIdentity,
    writeCheckOutIdentity,
    getDocumentBytes: getPresentationAsPptx,
    describeSaveUnavailable,
  },

  assistant: powerPointAssistant,
};
